import { useGetAllStores } from "@/api/stores/get-all-stores";
import { useToggleStore } from "@/api/stores/toggle-store";
import NoDataFound from "@/components/layout/no-data-found";
import PageWrapper from "@/components/layout/page-wrapper";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import PaginationCombo from "@/components/ui/pagination-combo";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn, formatDate } from "@/lib/utils";
import { AlignLeft, ChevronDown, MonitorCheck, MonitorX, Search, SquareMenuIcon } from "lucide-react";
import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";

const StoreTableDropdown = ({ id, status }: { id: string; status: boolean }) => {
    const navigate = useNavigate();
    const { mutate } = useToggleStore();
    return (
        <DropdownMenu>
            <DropdownMenuTrigger>
                <Button size={"sm"} variant="ghost" className="p-2 cursor-pointer">
                    <SquareMenuIcon />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("store/" + id)}>
                    <AlignLeft /> Store Details
                </DropdownMenuItem>
                {status ? (
                    <DropdownMenuItem onClick={() => mutate({ id })}>
                        <MonitorX /> Disable Store
                    </DropdownMenuItem>
                ) : (
                    <DropdownMenuItem onClick={() => mutate({ id })}>
                        <MonitorCheck /> Enable Store
                    </DropdownMenuItem>
                )}
                {/* <DropdownMenuItem>
                    <LogInIcon /> Login as Store Owner
                </DropdownMenuItem> */}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const StoreManagement = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Extract parameters from URL - React Query should refetch when these change
    const skip = (parseInt(searchParams.get("page") || "1") - 1) * parseInt(searchParams.get("per_page") || "10");
    const limit = parseInt(searchParams.get("per_page") || "10");
    const search = searchParams.get("search") || null;
    const isActiveParam = searchParams.get("is_active");
    const is_active = isActiveParam === "true" ? true : isActiveParam === "false" ? false : undefined;

    // This hook will refetch when any of these parameters change
    const {
        data: stores,
        isLoading,
        isError,
    } = useGetAllStores({
        skip,
        limit,
        search,
        is_active,
    });

    const formRef = useRef<HTMLFormElement>(null);
    const statusRef = useRef<HTMLSelectElement>(null);

    useEffect(() => {
        if (searchParams.get("search") && formRef.current) {
            formRef.current.search.value = searchParams.get("search") || "";
        }

        // Set the initial status dropdown value based on URL parameters
        if (statusRef.current) {
            const currentStatus = searchParams.get("is_active");
            if (currentStatus === "true" || currentStatus === "false") {
                statusRef.current.value = currentStatus;
            } else {
                statusRef.current.value = "all";
            }
        }
    }, [searchParams]);

    const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = e.target as HTMLFormElement;
        const searchValue = form.search.value.trim();
        const statusValue = statusRef.current?.value || "all";

        const params: Record<string, string> = {
            page: "1",
            per_page: "10",
        };

        if (searchValue) {
            params.search = searchValue;
        }

        if (statusValue !== "all") {
            params.is_active = statusValue;
        }

        setSearchParams(params);
    };

    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const target = e.target as HTMLSelectElement;
        const statusValue = target.value;
        const searchValue = formRef.current?.search.value.trim() || "";

        const params: Record<string, string> = {
            page: "1",
            per_page: "10",
        };

        if (searchValue) {
            params.search = searchValue;
        }

        if (statusValue !== "all") {
            params.is_active = statusValue;
        }

        setSearchParams(params);
    };

    const clearFilters = () => {
        setSearchParams({ page: "1", per_page: "10" });
        if (formRef.current) {
            formRef.current.search.value = "";
        }
        if (statusRef.current) {
            statusRef.current.value = "all";
        }
    };

    return (
        <PageWrapper pageTitle="Store Management" currentPage="Store Management">
            <div>
                <Card className="mt-4 p-4">
                    <div className="flex justify-end">
                        <div className="flex gap-2 items-center">
                            <form ref={formRef} className="flex border rounded-md" onSubmit={handleSearchSubmit}>
                                <Input placeholder="search" className="border-none" name="search" />
                                <Button variant={"ghost"} type="submit">
                                    <Search />
                                </Button>
                            </form>

                            <div className="relative">
                                <select
                                    ref={statusRef}
                                    onChange={handleStatusChange}
                                    className="w-full rounded-md border border-input bg-background py-2 pl-3 pr-8 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&_[data-icon]]:size-4 appearance-none"
                                >
                                    <option value="all">All Status</option>
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none" />
                            </div>

                            {(searchParams.get("search") || searchParams.get("is_active")) && <Button onClick={clearFilters}>Clear</Button>}
                        </div>
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {/* <TableHead>
                                    <Checkbox />
                                </TableHead> */}
                                <TableHead>Store ID</TableHead>
                                <TableHead>Store Link</TableHead>
                                <TableHead>Store Owner</TableHead>
                                <TableHead>Platform Type</TableHead>
                                <TableHead>Subscribed Plan</TableHead>
                                <TableHead>Created At</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Actions</TableHead>

                                {/* <TableHead>Action</TableHead> */}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {stores?.data?.map(
                                (item: {
                                    id: string;
                                    name: string;
                                    owner_name: string;
                                    platform_type: string;
                                    subscribed_plan: string;
                                    created_at: string;
                                    is_active: boolean;
                                }) => (
                                    <TableRow key={item.id}>
                                        <TableCell>{item?.id}</TableCell>
                                        <TableCell className="font-bold text-blue-500 underline">{item?.name}</TableCell>
                                        <TableCell>{item?.owner_name}</TableCell>
                                        <TableCell>{item?.platform_type ?? "Custom"}</TableCell>
                                        <TableCell>{item?.subscribed_plan ?? "No Package"}</TableCell>
                                        <TableCell>{formatDate(new Date(item?.created_at))}</TableCell>
                                        <TableCell className={cn(item?.is_active ? "text-green-700" : "text-red-700", " font-bold")}>
                                            {item?.is_active ? "Active" : "Disabled"}
                                        </TableCell>
                                        <TableCell>
                                            <StoreTableDropdown id={item?.id} status={item?.is_active} />
                                        </TableCell>
                                    </TableRow>
                                )
                            )}

                            {isLoading &&
                                [1, 2, 3, 4, 5, 6, 7, 8, 9].map((item) => (
                                    <TableRow key={item}>
                                        <TableCell className="p-4 font-medium">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                        <TableCell className="p-4">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                        <TableCell className="p-4">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                        <TableCell className="p-4 font-medium">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                        <TableCell className="p-4">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                        <TableCell className="p-4">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                        <TableCell className="p-4">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                        <TableCell className="p-4">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {isError && (
                                <TableRow>
                                    <TableCell colSpan={8} className="w-full text-center">
                                        <NoDataFound message="No Leads Found" />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    {!isLoading && !isError && (
                        <>
                            <PaginationCombo totalItems={stores?.pagination?.total_entity} />
                        </>
                    )}
                </Card>
            </div>
        </PageWrapper>
    );
};

export default StoreManagement;
